<script setup lang="ts">
  import '@/assets/calendar-legal-os.css'
  import CalendarEventChipContent from '@/components/app/calendar/CalendarEventChipContent.vue'
  import {
    isNowVisibleOnTimeGrid,
    useCalendarNowIndicator
  } from '@/composables/useCalendarNowIndicator'
  import { useCalendarTimeGrid } from '@/composables/useCalendarTimeGrid'
  import type { CalendarViewId } from '@/constants/calendar/views'
  import type { CalendarGridEvent } from '@/types/calendar/grid'
  import {
    CALENDAR_HOURS,
    CALENDAR_PX_PER_MIN,
    eventChipStyle,
    formatCalendarHour,
    isCompactCalendarEvent,
    isUltraCompactCalendarEvent
  } from '@/utils/calendar/event-layout'
  import {
    CALENDAR_EVENT_CHIP_CLASS,
    type CalendarEventTypeLabels
  } from '@/utils/calendar/event-display'
  import { computed, ref, toRef } from 'vue'

  const props = defineProps<{
    selectedDate: Date
    activeView: Extract<CalendarViewId, 'day' | 'week'>
    eventsByDate: Map<string, CalendarGridEvent[]>
    weekdayLabels: string[]
    allDayLabel: string
    eventTypeLabels: CalendarEventTypeLabels
    canDragEvents: boolean
  }>()

  const eventsByDateRef = computed(() => props.eventsByDate)
  const weekdayAbbrs = computed(() => props.weekdayLabels)
  const selectedDateRef = toRef(props, 'selectedDate')
  const activeViewRef = toRef(props, 'activeView')
  const emit = defineEmits<{
    'open-event': [event: CalendarGridEvent]
    'request-move-event': [value: { eventUuid: string; start: string; end: string }]
  }>()

  const {
    visibleDays,
    visibleDayCols,
    weekDays,
    eventsForDay,
    eventsForWeekday,
    isDayEmpty,
    isWeekEmpty,
    isWeekend
  } = useCalendarTimeGrid(selectedDateRef, activeViewRef, eventsByDateRef, weekdayAbbrs)

  const { now, nowIndicatorTopPx, timezoneLabel } = useCalendarNowIndicator()

  const showNowIndicator = computed(() =>
    isNowVisibleOnTimeGrid(
      now.value,
      props.activeView,
      props.selectedDate,
      weekDays.value.map((day) => day.date)
    )
  )

  const hours = CALENDAR_HOURS
  const draggedEventUuid = ref<string | null>(null)
  const suppressOpenUntil = ref(0)

  const visibleEvents = computed(() =>
    props.activeView === 'day'
      ? eventsForDay.value
      : weekDays.value.flatMap((_, index) => eventsForWeekday(index))
  )

  const buildMovedRange = (
    targetDate: Date,
    clientY: number,
    container: HTMLElement,
    event: CalendarGridEvent
  ): { start: string; end: string } => {
    const minutesFromTop = Math.max(0, clientY - container.getBoundingClientRect().top)
    const snappedStartMin = Math.max(
      0,
      Math.min(
        24 * 60 - event.durationMin,
        Math.round(minutesFromTop / CALENDAR_PX_PER_MIN / 15) * 15
      )
    )
    const start = new Date(targetDate)
    start.setHours(Math.floor(snappedStartMin / 60), snappedStartMin % 60, 0, 0)
    const end = new Date(start.getTime() + event.durationMin * 60_000)
    return {
      start: start.toISOString(),
      end: end.toISOString()
    }
  }

  const onEventDragStart = (dragEvent: DragEvent, event: CalendarGridEvent): void => {
    if (!props.canDragEvents || !event.canEdit || event.allDay) {
      dragEvent.preventDefault()
      return
    }

    draggedEventUuid.value = event.uuid
    dragEvent.dataTransfer?.setData('text/calendar-event-uuid', event.uuid)
    if (dragEvent.dataTransfer) {
      dragEvent.dataTransfer.effectAllowed = 'move'
    }
  }

  const onEventDragEnd = (): void => {
    draggedEventUuid.value = null
    suppressOpenUntil.value = Date.now() + 250
  }

  const requestMove = (
    dragEvent: DragEvent,
    targetDate: Date,
    container: HTMLElement | null
  ): void => {
    const eventUuid =
      dragEvent.dataTransfer?.getData('text/calendar-event-uuid') || draggedEventUuid.value || ''
    const event = visibleEvents.value.find((candidate) => candidate.uuid === eventUuid)
    if (!event || !container) {
      return
    }

    const moved = buildMovedRange(targetDate, dragEvent.clientY, container, event)
    const isSameRange =
      Date.parse(moved.start) === Date.parse(event.start) &&
      Date.parse(moved.end) === Date.parse(event.end)
    if (isSameRange) {
      return
    }

    emit('request-move-event', {
      eventUuid: event.uuid,
      start: moved.start,
      end: moved.end
    })
  }

  const onGridDragOver = (dragEvent: DragEvent): void => {
    if (props.canDragEvents) {
      dragEvent.preventDefault()
    }
  }

  const openEvent = (event: CalendarGridEvent): void => {
    if (Date.now() < suppressOpenUntil.value) {
      return
    }

    emit('open-event', event)
  }
</script>

<template>
  <div
    class="calendar-main bg-layer relative flex min-h-0 flex-1 flex-col overflow-y-auto [scrollbar-width:thin]
      [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar]:w-2"
    data-cy="calendar-time-grid"
  >
    <div class="border-layer-line bg-layer sticky inset-x-0 top-0 z-10 border-b">
      <div class="border-layer-line border-b">
        <div class="flex flex-auto">
          <div class="border-layer-line flex shrink-0 flex-col border-e">
            <div class="flex h-8 w-20 items-center justify-end text-end">
              <span class="text-muted-foreground-1 pe-4 text-[11px]">{{ timezoneLabel }}</span>
            </div>
          </div>
          <div :class="['grid w-full', visibleDayCols]">
            <div
              v-for="d in visibleDays"
              :key="d.abbr"
              :class="[
                'inline-flex items-center gap-x-1 border-b-2 px-3 py-1 text-end text-xs',
                d.today
                  ? 'border-primary font-medium text-primary'
                  : 'border-transparent text-foreground'
              ]"
            >
              {{ d.abbr }}
              <span class="hidden font-semibold sm:block">{{ d.day }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-auto">
        <div class="border-layer-line flex shrink-0 flex-col border-e">
          <div class="flex h-10 w-20 items-center justify-end text-end">
            <span class="text-muted-foreground-1 pe-4 text-[11px]">{{ allDayLabel }}</span>
          </div>
        </div>
        <div :class="['grid w-full', visibleDayCols]">
          <div
            v-for="(d, dIdx) in visibleDays"
            :key="`${d.abbr}-allday`"
            :class="[
              'border-layer-line relative flex h-10 flex-1 border-e last:border-e-0',
              activeView === 'week' && isWeekend(dIdx)
                ? 'after:bg-surface/40 after:pointer-events-none after:absolute after:inset-0 after:z-0'
                : ''
            ]"
          >
            <div class="w-full" />
          </div>
        </div>
      </div>
    </div>

    <div class="relative flex flex-col">
      <div class="-mt-px flex flex-auto">
        <div class="border-layer-line flex shrink-0 flex-col border-e">
          <div
            v-for="(h, idx) in hours"
            :key="h"
            :class="['h-14 w-20 text-end', idx === 0 ? 'opacity-0' : '']"
          >
            <span class="text-muted-foreground-1 relative -top-[7px] pe-4 text-[11px]">
              {{ formatCalendarHour(h) }}
            </span>
          </div>
        </div>

        <div
          v-if="activeView === 'day'"
          class="relative w-full"
          @dragover="onGridDragOver"
          @drop.prevent="requestMove($event, selectedDate, $event.currentTarget as HTMLElement)"
        >
          <div
            v-if="showNowIndicator"
            class="pointer-events-none absolute left-0 right-2 z-[8] border-t-2 border-rose-500 after:-ml-1.5
              after:-mt-[7px] after:block after:size-3 after:rounded-full after:bg-rose-500"
            :style="{ top: `${nowIndicatorTopPx}px` }"
          />

          <div
            v-for="h in hours"
            :key="`row-${h}`"
            class="after:border-layer-line relative h-14 pe-2 after:pointer-events-none after:absolute after:inset-x-0
              after:top-0 after:block after:border-t"
          />

          <template v-if="!isDayEmpty">
            <div
              v-for="(ev, i) in eventsForDay"
              :key="i"
              :draggable="props.canDragEvents && ev.canEdit && !ev.allDay"
              :style="eventChipStyle(ev)"
              :class="[
                'absolute z-[8] overflow-hidden rounded-sm px-2 text-start text-xs',
                CALENDAR_EVENT_CHIP_CLASS,
                isUltraCompactCalendarEvent(ev) ? 'flex items-center py-0' : 'flex flex-col py-1'
              ]"
              @click="openEvent(ev)"
              @dragstart="onEventDragStart($event, ev)"
              @dragend="onEventDragEnd"
            >
              <CalendarEventChipContent
                v-if="!isCompactCalendarEvent(ev)"
                :event="ev"
                :event-type-labels="eventTypeLabels"
              />
              <span
                v-else
                class="block w-full truncate font-medium leading-tight"
              >
                {{ ev.title }}
              </span>
            </div>
          </template>
        </div>

        <div
          v-else
          class="relative w-full"
        >
          <div
            v-if="showNowIndicator"
            class="pointer-events-none absolute left-0 right-2 z-[8] border-t-2 border-rose-500 after:-ml-1.5
              after:-mt-[7px] after:block after:size-3 after:rounded-full after:bg-rose-500"
            :style="{ top: `${nowIndicatorTopPx}px` }"
          />

          <div class="grid h-full grid-cols-7">
            <div
              v-for="(d, dIdx) in weekDays"
              :key="d.abbr"
              class="relative flex flex-col"
              @dragover="onGridDragOver"
              @drop.prevent="requestMove($event, d.date, $event.currentTarget as HTMLElement)"
            >
              <div
                v-for="h in hours"
                :key="`${d.abbr}-${h}`"
                :class="[
                  'border-layer-line relative h-14 border-b border-e',
                  isWeekend(dIdx)
                    ? 'after:bg-surface/40 after:pointer-events-none after:absolute after:inset-0 after:z-0'
                    : ''
                ]"
              />

              <template v-if="!isWeekEmpty">
                <div
                  v-for="(ev, i) in eventsForWeekday(dIdx)"
                  :key="i"
                  :draggable="props.canDragEvents && ev.canEdit && !ev.allDay"
                  :style="eventChipStyle(ev, 2)"
                  :class="[
                    'absolute z-[8] overflow-hidden rounded-sm px-1.5 text-start text-xs',
                    CALENDAR_EVENT_CHIP_CLASS,
                    isUltraCompactCalendarEvent(ev)
                      ? 'flex items-center py-0'
                      : 'flex flex-col py-1'
                  ]"
                  @click="openEvent(ev)"
                  @dragstart="onEventDragStart($event, ev)"
                  @dragend="onEventDragEnd"
                >
                  <CalendarEventChipContent
                    v-if="!isCompactCalendarEvent(ev)"
                    :event="ev"
                    :event-type-labels="eventTypeLabels"
                  />
                  <span
                    v-else
                    class="block w-full truncate font-medium leading-tight"
                  >
                    {{ ev.title }}
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
